import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge, Collapse, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagic, faSpinner, faChevronDown, faChevronRight, faDollarSign, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

/**
 * Cost estimation constants - based on functions/utils/cost-estimation.js
 * Simplified for frontend display (server-side calculation is authoritative)
 */
const COST_PER_MODE = {
    title: 0.008,
    description: 0.015,
    metadata: 0.012,
    weight: 0.006
};

/**
 * Estimates cost for a batch of products across selected modes.
 * @param {number} productCount - Number of products to enrich
 * @param {string[]} modes - Array of selected mode keys
 * @param {object} skipCounts - Skip counts per mode
 * @returns {object} Cost breakdown with total and per-mode costs
 */
const estimateCost = (productCount, modes, skipCounts = {}) => {
    const breakdown = [];
    let totalCost = 0;
    let totalToProcess = 0;

    modes.forEach(mode => {
        const skipped = skipCounts[mode] || 0;
        const toProcess = Math.max(0, productCount - skipped);
        const modeCost = (COST_PER_MODE[mode] || 0.01) * toProcess;

        breakdown.push({
            mode,
            cost: modeCost,
            count: toProcess,
            skipped
        });

        totalCost += modeCost;
        totalToProcess += toProcess;
    });

    return {
        totalCost,
        totalToProcess,
        averagePerProduct: totalToProcess > 0 ? totalCost / totalToProcess : 0,
        breakdown
    };
};

/**
 * Formats cost as currency string.
 * @param {number} cost - Cost in USD
 * @returns {string} Formatted cost string
 */
const formatCost = (cost) => {
    if (cost < 0.01) {
        return `$${cost.toFixed(4)}`;
    }
    return `$${cost.toFixed(2)}`;
};

/**
 * Modal for selecting AI enrichment modes to run on selected products.
 *
 * Features:
 * - Mode selection with checkbox cards
 * - Skip counts per mode (products that already have enrichment)
 * - Override confirmation for title/description when products already enriched
 * - Cost estimation with breakdown
 * - Loading states for async operations
 */
export const EnrichmentModeModal = ({
    show,
    onClose,
    onConfirm,
    selectedCount,
    selectedProducts = [],
    progressJobs = {},
    onCalculateSkipCounts,
    isLoading = false
}) => {
    const [selectedModes, setSelectedModes] = useState({
        metadata: true,
        title: true,
        description: true,
        weight: true
    });

    // Skip count state
    const [skipCounts, setSkipCounts] = useState({});
    const [skipCountsLoading, setSkipCountsLoading] = useState(false);

    // Cost breakdown visibility
    const [showBreakdown, setShowBreakdown] = useState(false);

    // Override confirmation
    const [overrideConfirmed, setOverrideConfirmed] = useState(false);

    const enrichmentModes = [
        {
            key: 'metadata',
            label: 'Metadata',
            description: 'Extract ingredients, allergens, nutrition facts, product type, and flavor information'
        },
        {
            key: 'title',
            label: 'Title Optimization',
            description: 'Generate optimized product titles for marketplaces'
        },
        {
            key: 'description',
            label: 'Description Generation',
            description: 'Create compelling HTML product descriptions'
        },
        {
            key: 'weight',
            label: 'Weight Estimation',
            description: 'Estimate product weight in ounces and grams'
        }
    ];

    // Count products that already have enriched titles/descriptions
    const overrideCounts = useMemo(() => {
        let titleCount = 0;
        let descriptionCount = 0;

        const seen = new Set();
        selectedProducts.forEach(product => {
            const sku = product.parentSku || product.sku;
            if (!sku || seen.has(sku)) return;
            seen.add(sku);

            const job = progressJobs[sku];

            // Check for existing AI title (from live job or persisted enrichment)
            const hasTitle = job?.results?.title?.data?.optimized_title
                || product.enrichment?.title?.optimized_title;
            if (hasTitle) titleCount++;

            // Check for existing AI description
            const hasDesc = job?.results?.description?.data?.html_description
                || job?.results?.description?.data?.body_html
                || product.enrichment?.description?.html_description;
            if (hasDesc) descriptionCount++;
        });

        return { title: titleCount, description: descriptionCount };
    }, [selectedProducts, progressJobs]);

    // Determine if override warning should be shown
    const needsOverrideConfirmation = useMemo(() => {
        return (selectedModes.title && overrideCounts.title > 0)
            || (selectedModes.description && overrideCounts.description > 0);
    }, [selectedModes.title, selectedModes.description, overrideCounts]);

    // Get selected mode keys
    const getSelectedModeKeys = useCallback(() => {
        return Object.entries(selectedModes)
            .filter(([_, enabled]) => enabled)
            .map(([mode]) => mode);
    }, [selectedModes]);

    // Calculate skip counts when modal opens or products change
    useEffect(() => {
        const calculateSkips = async () => {
            if (!show || !onCalculateSkipCounts) return;
            if (!selectedProducts || selectedProducts.length === 0) return;

            setSkipCountsLoading(true);
            try {
                const skus = selectedProducts.map(p => p.parentSku || p.sku).filter(Boolean);
                const uniqueSkus = [...new Set(skus)];
                const modes = enrichmentModes.map(m => m.key);
                const counts = await onCalculateSkipCounts(uniqueSkus, modes);
                setSkipCounts(counts || {});
            } catch (error) {
                console.error('Error calculating skip counts:', error);
                setSkipCounts({});
            } finally {
                setSkipCountsLoading(false);
            }
        };

        calculateSkips();
    }, [show, selectedProducts, onCalculateSkipCounts]);

    // Reset state when modal closes
    useEffect(() => {
        if (!show) {
            setShowBreakdown(false);
            setOverrideConfirmed(false);
        }
    }, [show]);

    // Reset override confirmation when modes change
    useEffect(() => {
        setOverrideConfirmed(false);
    }, [selectedModes.title, selectedModes.description]);

    const handleModeChange = (mode) => {
        setSelectedModes(prev => ({
            ...prev,
            [mode]: !prev[mode]
        }));
    };

    const handleConfirm = () => {
        const modes = getSelectedModeKeys();
        onConfirm(modes);
    };

    const hasSelectedModes = Object.values(selectedModes).some(v => v);

    // Calculate cost estimate
    const selectedModeKeys = getSelectedModeKeys();
    const costEstimate = estimateCost(selectedCount, selectedModeKeys, skipCounts);

    // Calculate products to process: use min skip so button is enabled
    // if ANY selected mode has work to do
    const skipValues = selectedModeKeys.map(m => skipCounts[m] || 0);
    const minSkip = skipValues.length > 0 ? Math.min(...skipValues) : 0;
    const productsToProcess = Math.max(0, selectedCount - minSkip);

    // Build override warning message parts
    const overrideParts = [];
    if (selectedModes.title && overrideCounts.title > 0) {
        overrideParts.push(`${overrideCounts.title} product${overrideCounts.title !== 1 ? 's' : ''} with existing AI titles`);
    }
    if (selectedModes.description && overrideCounts.description > 0) {
        overrideParts.push(`${overrideCounts.description} product${overrideCounts.description !== 1 ? 's' : ''} with existing AI descriptions`);
    }

    // Confirm button disabled logic
    const confirmDisabled = !hasSelectedModes || isLoading || productsToProcess === 0
        || (needsOverrideConfirmation && !overrideConfirmed);

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={faMagic} className="me-2 text-primary" />
                    Run AI Enrichment
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info" className="mb-3">
                    <small>
                        Select enrichment modes to run on <strong>{selectedCount.toLocaleString()}</strong> product(s).
                        AI will analyze product images and existing data to extract information.
                    </small>
                </Alert>

                <Form>
                    {enrichmentModes.map(mode => {
                        const skipCount = skipCounts[mode.key] || 0;
                        const isSelected = selectedModes[mode.key];

                        return (
                            <div
                                key={mode.key}
                                className={`p-3 mb-2 border rounded cursor-pointer ${
                                    isSelected ? 'border-primary bg-light' : ''
                                }`}
                                onClick={() => handleModeChange(mode.key)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                        <Form.Check
                                            type="checkbox"
                                            id={`mode-${mode.key}`}
                                            label={<span className="fw-bold">{mode.label}</span>}
                                            checked={isSelected}
                                            onChange={() => handleModeChange(mode.key)}
                                        />
                                        <small className="text-muted d-block mt-1 ms-4">
                                            {mode.description}
                                        </small>
                                    </div>
                                    <div className="text-end ms-2">
                                        {skipCountsLoading ? (
                                            <Spinner animation="border" size="sm" variant="secondary" />
                                        ) : skipCount > 0 ? (
                                            <Badge bg="secondary" className="ms-2">
                                                {skipCount} will skip
                                            </Badge>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </Form>

                {!hasSelectedModes && (
                    <Alert variant="warning" className="mt-3 mb-0">
                        Please select at least one enrichment mode.
                    </Alert>
                )}

                {/* Override Warning */}
                {needsOverrideConfirmation && (
                    <Alert variant="warning" className="mt-3 mb-0">
                        <div className="d-flex align-items-start">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 mt-1 flex-shrink-0" />
                            <div>
                                <strong>Existing enrichment will be replaced</strong>
                                <div className="mt-1">
                                    <small>
                                        {overrideParts.join(' and ')} will be re-generated.
                                        The new AI results will replace the current values.
                                    </small>
                                </div>
                                <Form.Check
                                    type="checkbox"
                                    id="override-confirm"
                                    className="mt-2"
                                    label={<small className="fw-semibold">Yes, override existing titles/descriptions</small>}
                                    checked={overrideConfirmed}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        setOverrideConfirmed(!overrideConfirmed);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    </Alert>
                )}

                {/* Cost Estimate Section */}
                {hasSelectedModes && !needsOverrideConfirmation && (
                    <div className="mt-3" />
                )}
                {hasSelectedModes && (
                    <Alert variant="light" className={`${needsOverrideConfirmation ? 'mt-2' : 'mt-3'} mb-0 border`}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <FontAwesomeIcon icon={faDollarSign} className="me-2 text-success" />
                                <strong>Estimated cost: </strong>
                                <span className="text-success fw-bold">
                                    {formatCost(costEstimate.totalCost)}
                                </span>
                                <span className="text-muted ms-2">
                                    for {costEstimate.totalToProcess.toLocaleString()} enrichment{costEstimate.totalToProcess !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-decoration-none"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowBreakdown(!showBreakdown);
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={showBreakdown ? faChevronDown : faChevronRight}
                                    className="me-1"
                                />
                                {showBreakdown ? 'Hide' : 'Show'} breakdown
                            </Button>
                        </div>

                        <Collapse in={showBreakdown}>
                            <div>
                                <Table size="sm" className="mt-3 mb-0">
                                    <thead>
                                        <tr>
                                            <th>Mode</th>
                                            <th className="text-end">Products</th>
                                            <th className="text-end">Skipped</th>
                                            <th className="text-end">Cost</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {costEstimate.breakdown.map(item => (
                                            <tr key={item.mode}>
                                                <td className="text-capitalize">{item.mode}</td>
                                                <td className="text-end">{item.count.toLocaleString()}</td>
                                                <td className="text-end text-muted">{item.skipped.toLocaleString()}</td>
                                                <td className="text-end">{formatCost(item.cost)}</td>
                                            </tr>
                                        ))}
                                        <tr className="fw-bold">
                                            <td>Total</td>
                                            <td className="text-end">{costEstimate.totalToProcess.toLocaleString()}</td>
                                            <td className="text-end text-muted">-</td>
                                            <td className="text-end text-success">{formatCost(costEstimate.totalCost)}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </Collapse>
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button
                    variant={needsOverrideConfirmation && overrideConfirmed ? 'warning' : 'primary'}
                    onClick={handleConfirm}
                    disabled={confirmDisabled}
                >
                    {isLoading ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                            Processing...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faMagic} className="me-2" />
                            {needsOverrideConfirmation && overrideConfirmed
                                ? `Override & Enrich ${productsToProcess.toLocaleString()} Product${productsToProcess !== 1 ? 's' : ''}`
                                : `Enrich ${productsToProcess.toLocaleString()} Product${productsToProcess !== 1 ? 's' : ''}`
                            }
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EnrichmentModeModal;
